import { FC, useState } from 'react';
import { Collapse, Tag } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  PlayCircleFilled,
  ToolOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;

/**
 * Props mirror CopilotKit v2's `useRenderTool` render prop shape — the
 * discriminated union with three states keyed off `status`. The component is
 * generic by design: it works for any tool name and any args / result shape.
 *
 * Source: `node_modules/@copilotkit/react-core/dist/copilotkit-DFaI4j2r.d.mts`
 *   - lines 1559–1580 (RenderToolProps for `useRenderTool`)
 *   - lines 1342–1369 (ReactToolCallRenderer.render — used by `useRenderToolCall()`
 *     consumer; field names `args` instead of `parameters`, but `useRenderTool`
 *     re-spreads `parameters: props.args` so the registration-side surface uses
 *     `parameters`)
 *
 * Status values are the string literals from the `ToolCallStatus` enum
 * (`@copilotkit/core/dist/index.d.mts:10–14`): "inProgress" | "executing" |
 * "complete". An additional "error" variant is added by the projection layer
 * so server-side tool failures render an error card; the v2 typings do not
 * expose a failed-tool-call discriminant directly.
 */
export type ChatGenerativeUIBlockStatus = 'inProgress' | 'executing' | 'complete' | 'error';

export interface ChatGenerativeUIBlockProps {
  name: string;
  toolCallId: string;
  parameters: unknown;
  status: ChatGenerativeUIBlockStatus;
  result?: string;
  errorMessage?: string;
}

const STATUS_LABEL: Record<ChatGenerativeUIBlockStatus, string> = {
  inProgress: 'Preparing',
  executing: 'Executing',
  complete: 'Complete',
  error: 'Error'
};

const STATUS_COLOR: Record<ChatGenerativeUIBlockStatus, string> = {
  inProgress: 'blue',
  executing: 'processing',
  complete: 'success',
  error: 'error'
};

const StatusIcon: FC<{ status: ChatGenerativeUIBlockStatus }> = ({ status }) => {
  if (status === 'inProgress') return <LoadingOutlined />;
  if (status === 'executing') return <PlayCircleFilled />;
  if (status === 'complete') return <CheckCircleFilled />;
  return <CloseCircleFilled />;
};

const formatArgs = (parameters: unknown): string => {
  if (parameters === undefined || parameters === null) return '(none)';
  if (typeof parameters === 'string') {
    // Args may arrive as a partially-streamed JSON string during inProgress;
    // try to pretty-print, otherwise show raw.
    try {
      return JSON.stringify(JSON.parse(parameters), null, 2);
    } catch {
      return parameters;
    }
  }
  try {
    return JSON.stringify(parameters, null, 2);
  } catch {
    return String(parameters);
  }
};

const formatResult = (result: string | undefined): { kind: 'json' | 'text' | 'empty'; body: string } => {
  if (!result) return { kind: 'empty', body: '' };
  const trimmed = result.trim();
  if (!trimmed) return { kind: 'empty', body: '' };
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return { kind: 'json', body: JSON.stringify(JSON.parse(trimmed), null, 2) };
    } catch {
      // fall through to text
    }
  }
  return { kind: 'text', body: trimmed };
};

/**
 * Rich, inline visualization of an in-flight or completed tool call. Sits
 * alongside the at-a-glance `ChatToolCallChip`: the chip flags status; the
 * block exposes args (always) and the result (when complete). Both surfaces
 * coexist by design — see ADR-0002 §"`name: \"*\"` catch-all renderer ordering"
 * — so today's catch-all does not preempt tool-specific renderers Flow 1
 * will introduce later.
 * @param props - Render-prop union shaped like v2's `useRenderTool` registration.
 * @param props.name
 * @param props.toolCallId
 * @param props.parameters
 * @param props.status
 * @param props.result
 * @param props.errorMessage
 * @returns AntD card showing tool name, status, args, and (when complete) result.
 */
const ChatGenerativeUIBlock: FC<ChatGenerativeUIBlockProps> = ({
  name,
  toolCallId,
  parameters,
  status,
  result,
  errorMessage
}) => {
  const isComplete = status === 'complete';
  const isError = status === 'error';
  const [activeKeys, setActiveKeys] = useState<string | string[]>(isComplete || isError ? ['result'] : ['args']);

  const argsBody = formatArgs(parameters);
  const formattedResult = formatResult(result);

  return (
    <div
      role="region"
      aria-label={`Tool call ${name} ${STATUS_LABEL[status].toLowerCase()}`}
      data-testid="chat-generative-ui-block"
      data-tool-call-id={toolCallId}
      data-status={status}
      className="mt-1 mb-1 max-w-[85%] border border-gray-200 rounded-lg bg-white shadow-sm"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <ToolOutlined className="text-gray-500 shrink-0" />
          <span className="font-medium text-sm text-gray-900 truncate">{name}</span>
        </div>
        <Tag color={STATUS_COLOR[status]} icon={<StatusIcon status={status} />} className="!mr-0">
          <span className="text-xs">{STATUS_LABEL[status]}</span>
        </Tag>
      </div>
      <Collapse ghost activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys)} className="!bg-transparent">
        <Panel key="args" header={<span className="text-xs font-medium text-gray-700">Arguments</span>}>
          <pre className="text-xs bg-gray-50 rounded p-2 m-0 overflow-x-auto whitespace-pre-wrap break-words">
            {argsBody}
          </pre>
        </Panel>
        {(isComplete || isError) && (
          <Panel
            key="result"
            header={<span className="text-xs font-medium text-gray-700">{isError ? 'Error' : 'Result'}</span>}
          >
            {isError ? (
              <pre className="text-xs bg-red-50 text-red-800 rounded p-2 m-0 whitespace-pre-wrap break-words">
                {errorMessage || result || 'Tool call failed.'}
              </pre>
            ) : formattedResult.kind === 'empty' ? (
              <span className="text-xs text-gray-500 italic">No result returned.</span>
            ) : (
              <pre className="text-xs bg-gray-50 rounded p-2 m-0 overflow-x-auto whitespace-pre-wrap break-words">
                {formattedResult.body}
              </pre>
            )}
          </Panel>
        )}
      </Collapse>
    </div>
  );
};

export default ChatGenerativeUIBlock;
